import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import useSound from "use-sound";

import QuizLayout from "./QuizLayout";
import QuestionCard from "./QuestionCard";
import PreguntasTalentoSummary from "./PreguntasTalentoSummary";
import Loader from "../../UI/Loader";
import NoInternetScreen from "../../UI/NoInternetScreen";

import {
  getPTQuizStartThunk,
  getPTQuizCurrentThunk,
  sendPTQuizAnswerThunk,
  getPTQuizSummaryThunk,
} from "../../../store/preguntasTalentoQuiz/preguntasTalentoQuiz.thunks";
import {
  setRespuesta,
} from "../../../store/preguntasTalentoQuiz/preguntasTalentoQuiz.slice";

import clickSfx from "./click.mp3";
import "./css/fortalezaQuiz.css";

const PreguntasTalentoQuiz = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { currentFrase, respuestas, loading, summary, summaryLoading } =
    useSelector((state) => state.preguntasTalentoQuiz);

  const [playClick] = useSound(clickSfx, { volume: 0.25 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasError, setHasError] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [justFinished, setJustFinished] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Índice de la afirmación visible dentro de la (frase, talento) actual
  const [afirmacionIndex, setAfirmacionIndex] = useState(0);

  const afirmaciones = currentFrase?.afirmaciones || [];
  const totalAfirmaciones = afirmaciones.length;

  // Reset del índice cuando cambia (frase, talento). Reanuda en la primera no respondida.
  const posKey = `${currentFrase?.frase?.id || ""}-${currentFrase?.talento_index || ""}`;
  const prevPosKey = useRef(null);
  useEffect(() => {
    if (!currentFrase) return;
    if (prevPosKey.current === posKey) return;
    prevPosKey.current = posKey;
    const idx = afirmaciones.findIndex(
      (af) => respuestas[af.id] !== true && respuestas[af.id] !== false
    );
    setAfirmacionIndex(idx === -1 ? 0 : idx);
  }, [posKey, currentFrase, afirmaciones, respuestas]);

  // Carga inicial
  useEffect(() => {
    const load = async () => {
      try {
        const start = await dispatch(getPTQuizStartThunk()).unwrap();
        if (start?.todas_completadas) {
          setAlreadyCompleted(true);
          dispatch(getPTQuizSummaryThunk());
        } else {
          await dispatch(getPTQuizCurrentThunk()).unwrap();
        }
      } catch (e) {
        setHasError(true);
        toast.error("El quiz de fortalezas aún no está disponible", {
          position: "top-center",
          autoClose: 5000,
        });
        setTimeout(() => navigate("/mis-talentos"), 2000);
      } finally {
        setIsInitialLoading(false);
      }
    };
    load();
  }, [dispatch, navigate]);

  // Online/offline
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const submitAll = useCallback(
    async (respuestasMap) => {
      if (!currentFrase) return;
      setIsSubmitting(true);
      try {
        const payload = {
          respuestas: afirmaciones.map((af) => ({
            sub_frase_id: af.id,
            valor: respuestasMap[af.id],
          })),
        };

        const result = await dispatch(sendPTQuizAnswerThunk(payload));

        if (result.type.endsWith("/rejected")) {
          toast.error(result.payload || "Error al guardar las respuestas", {
            position: "top-center",
          });
          return;
        }

        if (result.payload?.completado && result.payload?.todas_completadas) {
          toast.success("¡Has completado el quiz!", {
            position: "top-center",
            autoClose: 3000,
          });
          setJustFinished(true);
          dispatch(getPTQuizSummaryThunk());
        }
      } catch (e) {
        toast.error("Error al guardar las respuestas", {
          position: "top-center",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentFrase, afirmaciones, dispatch]
  );

  const handlePick = useCallback(
    (subFraseId, valor) => {
      if (isSubmitting) return;
      playClick();
      dispatch(setRespuesta({ subFraseId, valor }));
    },
    [isSubmitting, playClick, dispatch]
  );

  const handleNext = useCallback(() => {
    if (isSubmitting) return;
    const afirmacion = afirmaciones[afirmacionIndex];
    if (!afirmacion) return;
    const valor = respuestas[afirmacion.id];
    if (valor !== true && valor !== false) return;

    if (afirmacionIndex < totalAfirmaciones - 1) {
      setAfirmacionIndex((i) => i + 1);
    } else {
      submitAll(respuestas);
    }
  }, [
    isSubmitting,
    afirmaciones,
    afirmacionIndex,
    totalAfirmaciones,
    respuestas,
    submitAll,
  ]);

  if (!isOnline) return <NoInternetScreen />;

  if (hasError) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <div className="fortaleza-error">
              <Icon icon="mdi:alert-circle" />
              <h3>Funcionalidad en desarrollo</h3>
              <p>El quiz estará disponible próximamente.</p>
            </div>
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  if (justFinished || alreadyCompleted) {
    return (
      <div className="dashboard-main-body">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
          <h6 className="fw-semibold mb-0">
            {justFinished ? "¡Fortalezas completadas!" : "Tus respuestas"}
          </h6>
          <ul className="d-flex align-items-center gap-2">
            <li className="fw-medium">
              <a
                href="/#/mis-talentos"
                className="d-flex align-items-center gap-1 hover-text-primary"
              >
                <Icon icon="mdi:arrow-left" className="text-lg" />
                Mis Talentos
              </a>
            </li>
          </ul>
        </div>

        {summaryLoading || !summary ? (
          <div className="card radius-12">
            <div className="card-body p-24">
              <Loader
                center
                size="lg"
                type="dots"
                delay={150}
                text="Cargando tus respuestas..."
              />
            </div>
          </div>
        ) : (
          <PreguntasTalentoSummary summary={summary} />
        )}
      </div>
    );
  }

  if (isInitialLoading || (!currentFrase && loading)) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <Loader
              overlay
              size="lg"
              type="dots"
              delay={150}
              text="Cargando quiz..."
            />
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  if (!currentFrase || !currentFrase.frase || totalAfirmaciones === 0) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <Loader overlay size="lg" type="dots" delay={150} text="Cargando..." />
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  const {
    frase,
    talento,
    opciones = [],
    frase_index,
    total_frases,
    talento_index,
    total_talentos,
    es_ultima_frase,
    es_ultimo_talento,
  } = currentFrase;

  const idxSeguro = Math.min(afirmacionIndex, totalAfirmaciones - 1);
  const afirmacion = afirmaciones[idxSeguro];
  const current = respuestas[afirmacion.id];

  const opcionSi = opciones.find((o) => o.valor === true) || { texto: "Sí" };
  const opcionNo = opciones.find((o) => o.valor === false) || { texto: "No" };
  const esUltimoPasoGlobal = es_ultima_frase && es_ultimo_talento;
  const esUltimaAfirmacion = idxSeguro === totalAfirmaciones - 1;
  const yaRespondida = current === true || current === false;

  return (
    <QuizLayout>
      <div className="quiz-scope">
        <QuestionCard>
          {(loading || isSubmitting) && (
            <Loader
              overlay
              size="lg"
              type="dots"
              delay={150}
              text={isSubmitting ? "Guardando respuesta..." : "Cargando..."}
            />
          )}

          <div className="fortaleza-header">
            <div className="fortaleza-progress">
              <Icon icon="mdi:progress-check" />
              <span className="fortaleza-progress-text">
                Fortaleza {frase_index}/{total_frases}
              </span>
            </div>
          </div>

          <div className="fortaleza-titulo-wrap">
            <h4 className="fortaleza-titulo">
              Fortaleza {frase.tipo_fortaleza} · {frase.texto}
            </h4>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${posKey}-${idxSeguro}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="pt-quiz-single"
            >
              <div
                className={`pt-quiz-card${yaRespondida ? " answered" : ""}`}
              >
                <p className="pt-quiz-card-text">{afirmacion.texto}</p>
                <div className="pt-quiz-card-opts">
                  <button
                    type="button"
                    className={`pt-quiz-opt pt-quiz-opt--si${
                      current === true ? " selected" : ""
                    }`}
                    onClick={() => handlePick(afirmacion.id, true)}
                    disabled={isSubmitting}
                  >
                    <Icon icon="mdi:check-circle-outline" />
                    <span>{opcionSi.texto}</span>
                  </button>
                  <button
                    type="button"
                    className={`pt-quiz-opt pt-quiz-opt--no${
                      current === false ? " selected" : ""
                    }`}
                    onClick={() => handlePick(afirmacion.id, false)}
                    disabled={isSubmitting}
                  >
                    <Icon icon="mdi:close-circle-outline" />
                    <span>{opcionNo.texto}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="pt-quiz-nav pt-quiz-nav--end">
            <button
              type="button"
              className="pt-quiz-next-btn"
              onClick={handleNext}
              disabled={!yaRespondida || isSubmitting}
            >
              <span>
                {esUltimaAfirmacion
                  ? esUltimoPasoGlobal
                    ? "Finalizar"
                    : "Guardar y continuar"
                  : "Siguiente"}
              </span>
              <Icon icon="mdi:arrow-right-circle" />
            </button>
          </div>
        </QuestionCard>
      </div>
    </QuizLayout>
  );
};

export default PreguntasTalentoQuiz;
